import React from "react";

import GeneralTab from "./Tabs/GeneralTab";
import AccordionGroup from "../../../../../../components/AccordionGroup";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import { Notes, definitions, itpProcedures } from "./utils";
import { itpColumns } from "./cols";
import ITPRowsTab from "./Tabs/ITPRowsTab";
import { SelectRev } from "./Tabs/Components/SelectRev";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  useCreateItp,
  useDeleteItp,
  useGetItpsByProjectAndType,
  useUpdateItp,
} from "./hooks";

const revs = ["Rev-00", "Rev-01"];

const ITP_TYPES = [
  "PRE-FABRICATION",
  "MANUFACTURING & NDE",
  "PWHT & NDE POST PWHT",
  "HYDROSTATIC TEST",
  "FINAL PREPARATION",
];

const itpKeys = [
  "GENERAL",
  "ITP TABLES",
  "STANDARD PROCEDURES",
  "PROJECT SPECIFICATION",
  "NOTES",
  "INSPECTOR FUNCTION DEFINITION",
];

export default function ITPTable({ plant = false, projectFilter = [] }) {
  const [rev, setRev] = React.useState(revs[0]);
  const [selectedType, setSelectedType] = React.useState(ITP_TYPES[0]);

  const handleRevChange = React.useCallback((event) => {
    setRev(event.target.value);
  }, []);

  const handleTypeChange = React.useCallback((event) => {
    setSelectedType(event.target.value);
  }, []);

  const useGetItemsByType = React.useMemo(() => {
    const bindType = (type) =>
      useGetItpsByProjectAndType.bind(null, type, plant, projectFilter);

    return {
      "PRE-FABRICATION": bindType("PRE-FABRICATION"),
      "MANUFACTURING & NDE": bindType("MANUFACTURING & NDE"),
      "PWHT & NDE POST PWHT": bindType("PWHT & NDE POST PWHT"),
      "HYDROSTATIC TEST": bindType("HYDROSTATIC TEST"),
      "FINAL PREPARATION": bindType("FINAL PREPARATION"),
    };
  }, [plant, projectFilter]);

  const useSelectedHook = useGetItemsByType[selectedType];

  const itpPanels = React.useMemo(
    () => [
      <GeneralTab generalData={{ rev }} />,

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>ITP Type</InputLabel>
            <Select
              value={selectedType}
              label="ITP Type"
              onChange={handleTypeChange}
            >
              {ITP_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <EditableTable
          key={selectedType}
          name={selectedType}
          columns={itpColumns}
          hooks={{
            useCreateItem: useCreateItp,
            useGetItems: useSelectedHook,
            useUpdateItem: useUpdateItp,
            useDeleteItem: useDeleteItp,
          }}
          enableCreate
          enableEditing
          enableArchive
        />
      </Box>,

      <ITPRowsTab rows={itpProcedures} />,
      "PROJECT SPECIFICATION",
      <ITPRowsTab rows={Notes} />,
      <ITPRowsTab rows={definitions} />,
    ],
    [rev, selectedType, handleTypeChange, useSelectedHook],
  );

  return (
    <AccordionGroup
      keys={itpKeys}
      panels={itpPanels}
      keysAsComponents={{
        GENERAL: (
          <Box sx={{ m: 0 }}>
            <Box sx={{ display: "inline-block" }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  pr: 2,
                  transform: "translateY(8px)",
                }}
              >
                GENERAL
              </Typography>
            </Box>

            <SelectRev
              rev={rev}
              handleRevChange={handleRevChange}
              revs={revs}
            />
          </Box>
        ),
      }}
    />
  );
}
